import { useEffect } from 'react';

const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://delegate.petergits.com';
const pageUrl = `${siteUrl}/dial-back`;
const title = 'Dial-Back by Delegate — AI Meeting Assistant That Calls You In';
const description =
  'Never miss a standup again. Jessica joins your conference bridge, holds your seat, and calls your phone directly — retrying until you answer. Unlimited dial-back for $49/month beta pricing.';
const keywords = 'dial-back, AI meeting assistant, conference call, Jessica AI, Delegate, meeting dial-in, flow state, missed meetings';

const buildJsonLd = (url: string) => [
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${url}/#webpage`,
    url,
    name: title,
    description,
    inLanguage: 'en',
    isPartOf: {
      '@type': 'WebSite',
      url: siteUrl,
    },
    dateModified: new Date().toISOString().split('T')[0],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Dial-Back by Delegate',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    url,
    description,
    offers: {
      '@type': 'Offer',
      price: '49',
      priceCurrency: 'USD',
      description: 'Beta pricing — unlimited dial-back per month.',
    },
    featureList: [
      'Reads your calendar automatically',
      'Joins conference bridge before meeting starts',
      'Calls your phone directly and retries until answered',
      'Silent mute-join via your bridge',
      'Platform-agnostic — works with Zoom, Teams, Meet, Webex',
      'Unlimited monthly bridge minutes',
    ],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'What is Dial-Back by Delegate?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Dial-Back is an AI meeting assistant (Jessica) that reads your calendar, joins your conference bridge before the meeting starts, and calls your phone directly — retrying until you answer — so you never miss a standup or meeting again.',
        },
      },
      {
        '@type': 'Question',
        name: 'How much does Dial-Back cost?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Dial-Back is available at $49/month during the beta period. This price is locked in for life for early subscribers. Unlimited dial-back is included with no recording or transcripts.',
        },
      },
      {
        '@type': 'Question',
        name: 'Which meeting platforms does Dial-Back support?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Dial-Back is platform-agnostic and works across Zoom, Microsoft Teams, Google Meet, Cisco Webex, and any other conference bridge you are invited to.',
        },
      },
      {
        '@type': 'Question',
        name: 'Does Dial-Back record my meetings?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'No. The current Dial-Back plan includes no recording and no transcripts — just your seat held and a direct phone callback. Recording and summary notes are coming soon as optional add-ons.',
        },
      },
    ],
  },
];

export default function DialBackSEO() {
  useEffect(() => {
    const jsonLd = buildJsonLd(pageUrl);

    // --- Title ---
    document.title = title;

    // --- Meta helpers ---
    const setMeta = (selector: string, attr: string, value: string) => {
      let el = document.querySelector<HTMLMetaElement>(selector);
      if (!el) {
        el = document.createElement('meta');
        const raw = selector.replace('meta[', '').replace(']', '');
        const eqIdx = raw.indexOf('="');
        const attrName = raw.substring(0, eqIdx);
        const attrVal = raw.substring(eqIdx + 2).replace(/"$/, '');
        el.setAttribute(attrName, attrVal);
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };

    // Standard meta
    setMeta('meta[name="description"]', 'content', description);
    setMeta('meta[name="keywords"]', 'content', keywords);
    setMeta('meta[name="robots"]', 'content', 'index, follow');
    setMeta('meta[name="last-modified"]', 'content', new Date().toISOString().split('T')[0]);

    // Canonical
    let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = pageUrl;

    // Open Graph
    setMeta('meta[property="og:title"]', 'content', title);
    setMeta('meta[property="og:description"]', 'content', description);
    setMeta('meta[property="og:url"]', 'content', pageUrl);
    setMeta('meta[property="og:type"]', 'content', 'website');
    setMeta('meta[property="og:site_name"]', 'content', 'Delegate');

    // Twitter
    setMeta('meta[name="twitter:card"]', 'content', 'summary_large_image');
    setMeta('meta[name="twitter:title"]', 'content', title);
    setMeta('meta[name="twitter:description"]', 'content', description);

    // JSON-LD
    const existingScripts = document.querySelectorAll('script[data-schema="dialback-jsonld"]');
    existingScripts.forEach((s) => s.remove());

    jsonLd.forEach((schema) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-schema', 'dialback-jsonld');
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    return () => {
      document.title = 'Jessica AI — Your Delegate';
      document.querySelectorAll('script[data-schema="dialback-jsonld"]').forEach((s) => s.remove());
    };
  }, []);

  return null;
}
